import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    updateComputerRetiredState,
    updateComputer as updateComputerRedux
} from '../../redux/slices/office-slice.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import useComputers from '../../hooks/useComputers.js';
import useTsPlusLicenses from '../../hooks/useTsPlusLicenses.js';
import useManualTsPlusLicenses from '../../hooks/useManualTsPlusLicenses.js';

/**
 * Component for displaying settings dropdown for a computer.
 *
 * @param {object} props - The props object.
 * @param {object} props.computer - The computer object.
 * @param {boolean} props.showEdit - Boolean indicating whether edit mode is active.
 * @param {function} props.setShowEdit - Function to toggle edit mode.
 * @param {boolean} props.showDuplicateComputerModal - Boolean indicating whether duplicate computer modal is visible.
 * @param {function} props.setShowDuplicateComputerModal - Function to toggle visibility of duplicate computer modal.
 * @param {string} props.officeSysName - The office system name.
 * @returns {JSX.Element} JSX element representing the Computer Settings Dropdown.
 */
const ComputerSettingsDropdown = ({
    computer,
    showEdit,
    setShowEdit,
    showDuplicateComputerModal,
    setShowDuplicateComputerModal,
    officeSysName
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { error, loading, updateComputer } = useComputers();
    const {
        error: licensesError,
        loading: licensesLoading,
        updateLicense
    } = useTsPlusLicenses();
    const { updateManualLicense } = useManualTsPlusLicenses();

    /**
     * Handler function for toggling the retired state of the computer.
     */
    const handleChangeActiveState = async () => {
        const updatedComputer = await updateComputer(
            computer._id,
            { retired: !computer.retired },
            computer,
            officeSysName
        );
        if (!updatedComputer) {
            return;
        }

        dispatch(
            updateComputerRetiredState({
                computerId: updatedComputer._id,
                retiredState: updatedComputer.retired,
                dateRetired: updatedComputer.dateRetired,
                retiredBy: updatedComputer.retiredBy
            })
        );
    };

    /**
     * Handles the change in HIPAA exemption status for OS.
     * @returns {void}
     */
    const handleChangeOsExempt = async () => {
        const updatedComputer = await updateComputer(
            computer._id,
            { hcOSExempt: !computer.hcOSExempt },
            computer,
            officeSysName
        );
        if (!updatedComputer) {
            return;
        }
        dispatch(
            updateComputerRedux({
                ...updatedComputer,
                credentials: computer.credentials
            })
        );
    };

    /**
     * Handles the change in HIPAA exemption status for passwords.
     * @returns {void}
     */
    const handleChangePasswordExempt = async () => {
        const updatedComputer = await updateComputer(
            computer._id,
            { hcPasswordsExempt: !computer.hcPasswordsExempt },
            computer,
            officeSysName
        );
        if (!updatedComputer) {
            return;
        }
        dispatch(
            updateComputerRedux({
                ...updatedComputer,
                credentials: computer.credentials
            })
        );
    };

    /**
     * Handles the change in HIPAA exemption status for AV.
     * @returns {void}
     */
    const handleChangeAvExempt = async () => {
        const updatedComputer = await updateComputer(
            computer._id,
            { hcAVExempt: !computer.hcAVExempt },
            computer,
            officeSysName
        );
        if (!updatedComputer) {
            return;
        }
        dispatch(
            updateComputerRedux({
                ...updatedComputer,
                credentials: computer.credentials
            })
        );
    };

    /**
     * Handles the release of a TSplus license for the computer.
     * @returns {void}
     */
    const handleReleaseLicense = async () => {
        // MANUAL
        if (computer.tsPlusLicenseType === 'MANUAL') {
            await updateManualLicense(computer.tsPlusLicenseId, {
                assignedComputerId: null
            });
        }

        // AUTO
        if (computer.tsPlusLicenseType === 'AUTO') {
            await updateLicense(computer.tsPlusLicenseId, {
                computerId: null
            });
        }

        // Just refresh UI / redux
        dispatch(
            updateComputerRedux({
                ...computer,
                tsPlusLicenseId: null,
                tsPlusLicenseType: null
            })
        );
    };

    return (
        <>
            <div className="computer-settings-dropdown-container">
                <div className="computer-settings-dropdown">
                    <p
                        className="computer-settings-dropdown-item"
                        onClick={() => setShowEdit(!showEdit)}>
                        {showEdit ? 'Cancel Edit' : 'Edit'}
                    </p>
                    <p
                        className="computer-settings-dropdown-item"
                        onClick={() => handleChangeActiveState()}>
                        {computer.retired ? 'Re-activate' : 'Retire'}
                    </p>
                    <p
                        className="computer-settings-dropdown-item"
                        onClick={() =>
                            setShowDuplicateComputerModal(
                                !showDuplicateComputerModal
                            )
                        }>
                        Duplicate
                    </p>
                    {!computer.tsPlusLicenseId && (
                        <p
                            className="computer-settings-dropdown-item"
                            onClick={() =>
                                navigate('/addTsPlusLicense', {
                                    state: {
                                        computerId: computer._id,
                                        computer,
                                        officeSysName
                                    }
                                })
                            }>
                            Add TSplus License
                        </p>
                    )}
                    {computer.tsPlusLicenseId && (
                        <p
                            className="computer-settings-dropdown-item"
                            onClick={() => handleReleaseLicense()}>
                            Release TSplus License
                        </p>
                    )}
                    <p
                        className="office-settings-dropdown-item"
                        onClick={() => handleChangeOsExempt()}>
                        {computer.hcOSExempt
                            ? 'Remove OS HIPAA exemption'
                            : 'Flag as exempt from OS HIPAA'}
                    </p>
                    <p
                        className="office-settings-dropdown-item"
                        onClick={() => handleChangePasswordExempt()}>
                        {computer.hcPasswordsExempt
                            ? 'Remove password HIPAA exemption'
                            : 'Flag as exempt from password HIPAA'}
                    </p>
                    <p
                        className="office-settings-dropdown-item"
                        onClick={() => handleChangeAvExempt()}>
                        {computer.hcAVExempt
                            ? 'Remove AV HIPAA Exemption'
                            : 'Flag as exempt from AV HIPAA'}
                    </p>
                </div>
            </div>
            {error && <ErrorMessage message={error.message} />}
            {loading && <LoadingSpinner />}
            {licensesLoading && <LoadingSpinner />}
            {licensesError && <ErrorMessage message={licensesError.message} />}
        </>
    );
};

ComputerSettingsDropdown.propTypes = {
    computer: PropTypes.object.isRequired,
    showEdit: PropTypes.bool.isRequired,
    setShowEdit: PropTypes.func.isRequired,
    showDuplicateComputerModal: PropTypes.bool.isRequired,
    setShowDuplicateComputerModal: PropTypes.func.isRequired,
    officeSysName: PropTypes.string.isRequired
};

export default ComputerSettingsDropdown;
